/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import React from "react";

import { Card, Button, List, ListItem, TextField, ListSubheader, ListItemText } from "@material-ui/core";
import { ConfirmDialog } from "../../../../components/ConfirmDialog/ConfirmDialog";
import { useState } from "react";

/**
 * This component houses the displayed list of the Third party integration application.
 * @param {(applicationId) => void} selectApplication This is a callback method for selecting a new application from the list
 * @param {() => void} createNewApplication This is a callback method for creating a new application to add to the list.
 * @param {Array} filteredAppConfigs The filtered list of applications to display on the card.
 * @param {String} selectedConfigId The ID of the currently selected application.
 * @param {Boolean} saving A boolean value denoting whether the form is saving.
 * @param {(String) => void} onSearchTermChange This is a callback method called when the search term changes.
 * @param {String} searchTermLabel A label for search term field.
 * @param {String} searchTerm The current searchTerm.
 */
export const ApplicationList = ({
	selectApplication,
	createNewApplication,
	filteredAppConfigList,
	selectedConfigId,
	saving,
	onSearchTermChange,
	searchTermLabel,
	searchTerm,
	labelText,
	emptyListText,
	formEdited,
}) => {
	const [selectedAppId, setSelectedAppId] = useState(false);
	const [confirmOpenState, setConfirmOpenState] = useState(""); // NEW, EDIT

	const onAppListClick = (appId) => {
		if (formEdited && appId !== selectedConfigId) {
			setConfirmOpenState("EDIT");
		} else {
			selectApplication(appId);
		}
		setSelectedAppId(appId);
	};

	const handleNewAppBtnClick = () => {
		if (formEdited) {
			setConfirmOpenState("NEW");
		} else {
			createNewApplication();
		}
	};

	return (
		<Card style={{ minHeight: 600, height: "100%", padding: "10px 5px" }}>
			<List
				component="nav"
				subheader={
					<ListSubheader disableSticky component="div">
						<div style={{ marginBottom: 8 }}>
							<TextField
								style={{ width: "100%" }}
								size="small"
								label={searchTermLabel}
								value={searchTerm}
								onChange={(e) => onSearchTermChange(e.target.value)}
							/>
						</div>
						<div className="d-flex flex-row justify-content-between align-items-center flex-wrap">
							<span>{labelText || "Applications"}</span>

							<Button
								variant="outlined"
								className="mb-2 mb-md-0"
								size="small"
								disabled={formEdited || saving}
								onClick={handleNewAppBtnClick}
							>
								Add
							</Button>
						</div>
					</ListSubheader>
				}
			>
				{filteredAppConfigList &&
					(filteredAppConfigList.length > 0 ? (
						filteredAppConfigList.map((config) => (
							<ListItem
								key={config.applicationId}
								selected={config.applicationId === selectedConfigId}
								button
								onClick={() => {
									onAppListClick(config.applicationId);
								}}
								title={!config.defaultApplication ? config.applicationId : undefined}
							>
								<ListItemText
									style={{ wordBreak: "break-word" }}
									primary={config.friendlyName}
									secondary={config.defaultApplication ? <span style={{ color: "gray" }}>Default</span> : ""}
								></ListItemText>
								{}
							</ListItem>
						))
					) : (
						<div style={{ margin: "auto", textAlign: "center", color: "gray" }}>
							{emptyListText || "No Applications Found"}
						</div>
					))}
			</List>
			<ConfirmDialog
				title={"Switch Application?"}
				message={"You have unsaved changes that will be discarded if you proceed. Are you sure?"}
				confirmOpen={!!confirmOpenState}
				onClose={(confirmed) => {
					if (confirmed) {
						if (confirmOpenState === "NEW") {
							createNewApplication();
						} else if (confirmOpenState === "EDIT") {
							selectApplication(selectedAppId);
						}
					}
					setConfirmOpenState("");
				}}
			/>
		</Card>
	);
};
